import * as React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import { Badge, Card, EmptyState, LoadingState, Select, Stat } from '@/components/ui/primitives';
import { formatCents, formatLocalDate } from '@/lib/utils';
import { WEBSITE_PROJECT_STATUSES } from '@shared/enums.js';

export function CustomersPage() {
  const [projectStatus, setProjectStatus] = React.useState('');

  const customers = useQuery({ queryKey: ['customers'], queryFn: api.customers });
  const totals = useQuery({ queryKey: ['money-totals'], queryFn: api.moneyTotals });

  const filtered = (customers.data ?? []).filter(
    (c) => !projectStatus || c.websiteProjectStatus === projectStatus,
  );

  return (
    <Page title="Customers" description="Sold leads, their projects, and the money against each one.">
      <div className="space-y-5 p-5 md:p-6">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Stat label="Customers" value={(customers.data?.length ?? 0).toLocaleString()} />
          <Stat
            label="Booked sales"
            value={formatCents(totals.data?.bookedSalesCents ?? 0)}
            hint="Total agreed sale value"
          />
          <Stat
            label="Collected payments"
            value={formatCents(totals.data?.collectedPaymentsCents ?? 0)}
            hint="Received, net of refunds"
            valueClassName="text-emerald-700 dark:text-emerald-300"
          />
          <Stat
            label="Outstanding"
            value={formatCents(totals.data?.outstandingBalanceCents ?? 0)}
            hint="Booked minus collected"
            valueClassName={(totals.data?.outstandingBalanceCents ?? 0) > 0 ? 'text-amber-300' : undefined}
          />
        </div>

        <div className="toolbar">
          <Select value={projectStatus} onChange={(e) => setProjectStatus(e.target.value)} className="w-48">
            <option value="">All project statuses</option>
            {WEBSITE_PROJECT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </div>

        {customers.isLoading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No customers yet"
            description="Convert a sold lead from its detail page to start tracking the project and payments."
          />
        ) : (
          <Card>
            <table className="data-table">
              <thead>
                <tr>
                  <th className="min-w-[14rem]">Business</th>
                  <th className="w-40">Service</th>
                  <th className="w-28">Sale date</th>
                  <th className="w-28 text-right">Booked</th>
                  <th className="w-28 text-right">Collected</th>
                  <th className="w-28 text-right">Outstanding</th>
                  <th className="w-36">Project</th>
                  <th className="w-28">Hosting</th>
                  <th className="w-40">Domain</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <Link to={`/leads/${customer.leadId}`} className="font-medium hover:text-primary hover:underline">
                        {customer.leadBusinessName ?? `Lead #${customer.leadId}`}
                      </Link>
                    </td>
                    <td className="truncate text-muted-foreground">{customer.serviceSold}</td>
                    <td>{formatLocalDate(customer.saleDate)}</td>
                    <td className="text-right tabular-nums">{formatCents(customer.saleAmountCents)}</td>
                    <td className="text-right tabular-nums text-emerald-700 dark:text-emerald-300">
                      {formatCents(customer.collectedCents ?? 0)}
                    </td>
                    <td
                      className={`text-right tabular-nums ${
                        (customer.outstandingCents ?? 0) > 0 ? 'text-amber-300' : 'text-muted-foreground'
                      }`}
                    >
                      {formatCents(customer.outstandingCents ?? 0)}
                    </td>
                    <td>
                      <Badge
                        variant={
                          customer.websiteProjectStatus === 'Live' || customer.websiteProjectStatus === 'Completed'
                            ? 'success'
                            : customer.websiteProjectStatus === 'On Hold'
                              ? 'warning'
                              : 'default'
                        }
                      >
                        {customer.websiteProjectStatus}
                      </Badge>
                    </td>
                    <td className="text-muted-foreground">{customer.hostingStatus}</td>
                    <td className="truncate text-muted-foreground">{customer.domainName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </Page>
  );
}
